import { isContentSized, SHEET_PEEK } from "./snap-points";
import type { BottomSheetSnapPoint, BottomSheetSnapPoints } from "./types";

/**
 * Vaul's snap point for a detent (a fraction of the sheet surface).
 *
 * The surface is bottom-aligned and capped at the viewport minus the peek
 * (`SHEET_PEEK`, as on native). Vaul moves it down by `viewport × (1 − snap)`,
 * and showing a fraction `detent` of it takes `(1 − detent) × surface`, so
 * `snap = 1 − (1 − detent) × surface / viewport`: "max" (1) maps to 1, the
 * surface resting with its top at the peek. The top safe-area inset is taken
 * as 0 here: the web apps don't extend under it (no `viewport-fit=cover`).
 */
export function toVaulSnap(detent: number, viewportHeight: number): number {
  if (viewportHeight <= 0) return detent;
  const surface = Math.max(0, viewportHeight - SHEET_PEEK);
  return 1 - ((1 - detent) * surface) / viewportHeight;
}

/** Vaul's snap points: none for a content-sized sheet (Vaul's default). */
export const toVaulSnapPoints = (
  points: BottomSheetSnapPoints,
  viewportHeight: number,
): number[] | undefined =>
  isContentSized(points) ? undefined : points.map((point) => toVaulSnap(point, viewportHeight));

/** Vaul's active snap point for the caller's, or undefined when content-sized. */
export function toVaulActiveSnapPoint(
  points: BottomSheetSnapPoints,
  active: BottomSheetSnapPoint | null | undefined,
  viewportHeight: number,
): number | null | undefined {
  if (isContentSized(points) || active === undefined) return undefined;
  if (active === null || active === "content") return null;
  return toVaulSnap(active, viewportHeight);
}

/**
 * The caller's snap point for the one Vaul reports, matched by index so float
 * drift in the conversion can't break the round trip.
 */
export function fromVaulSnapPoint(
  points: BottomSheetSnapPoints,
  vaulSnap: number | string | null,
  viewportHeight: number,
): BottomSheetSnapPoint | null {
  if (vaulSnap === null || isContentSized(points)) return null;
  const index = toVaulSnapPoints(points, viewportHeight)!.findIndex((point) => point === vaulSnap);
  return index < 0 ? null : points[index]!;
}
